import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '../../../components/general/Icon';
import { Label } from '../../../components/general/forms/Label';
import { Colors } from '../../../styles/colors';
import { ErrorText } from "./DefaultInput";

export interface Suggestion {
  id: string;
  text: string;
}

export interface SuggestionDropDownProps {
  id: string;
  label?: string;
  onChange: (suggestion: Suggestion) => void;
  placeholder?: string;
  value: Suggestion | null;
  suggestions: Suggestion[];
  // Error handling
  required?: boolean;
  errorMessage?: string;
  hasError?: boolean;
  showSearchResultsOnly?: boolean;

  // UI
  inline?: boolean | { dropdownInline: boolean, dropdownNegativeMargin: boolean, whiteBackground: boolean };
}

export const InputWrapper = styled.div<{ inline?: boolean | { dropdownInline: boolean, dropdownNegativeMargin: boolean, whiteBackground: boolean } }>`
  display: flex;
  flex-direction: column;
  width: 100%;

  padding-top: ${({ inline }) => Boolean(typeof inline === 'object' ? inline?.dropdownInline : inline) ? '0' : '2rem'};
`;

export const InputOuterWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  overflow: visible;
`;

export const InputInnerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: stretch;
  overflow: hidden;
`;

const DropDownWrapper = styled.div<{ inline?: boolean | { dropdownInline: boolean, dropdownNegativeMargin: boolean, whiteBackground: boolean } }>`
  display: flex;
  flex-direction: column;
  
  margin: ${({ inline }) => Boolean(typeof inline === 'object' ? inline?.dropdownNegativeMargin : inline) ? '1rem -1rem 0' : '0'};
`;

const OpenSelectButton = styled.button`
  background-color: ${Colors.primary};
  width: 3.625rem;
  padding: 1rem;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;


export const StyledInput = styled.input<{ inline?: boolean | { dropdownInline: boolean, dropdownNegativeMargin: boolean, whiteBackground: boolean } }>`
//  -webkit-appearance: none;
//  -moz-appearance: none;
  text-indent: 1px;
  text-overflow: '';
  border: none;
  color: ${Colors.gray};
  background-color: ${({ inline }) => Boolean(typeof inline === 'object' ? inline?.whiteBackground : inline) ? Colors.white : Colors.background};
  font-size: 0.875rem;
  padding: 0 4.625rem 0 1rem;
  flex: 1;
  min-height: 3.625rem;
  
  ::placeholder,
  ::-webkit-input-placeholder {
    color: ${Colors.gray};
    font-size: 0.875rem;
  }
  
  &.local-error {
    box-shadow: inset 0 0 0 1px ${Colors.error};
    padding-left: 1rem;
    color: ${Colors.error};
  }

  &:focus {
    outline: none;
  }
  
  &:hover {
    cursor: pointer;
  }
`;

const SuggestionList = styled.div<{ inline?: boolean | { dropdownInline: boolean, dropdownNegativeMargin: boolean, whiteBackground: boolean } }>`
  margin-right: 3.625rem;
  display: flex;
  flex-direction: column;
  background-color: ${Colors.background};
  padding: 1rem;
  pointer-events: all;
  align-items: flex-start;
  margin-top: 1px;
  
  
  ${({ inline }) => Boolean(typeof inline === 'object' ? inline?.dropdownInline : inline) ? `
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 1px;
    background-color: ${Colors.white};
  ` : ''}
`;

const SuggestionListItem = styled.button`
  margin-bottom: 1rem; 
  color: ${Colors.grayDark};
  pointer-events: all;

  &:last-child {
    margin-bottom: 0; 
  }
  
  &:hover {
    cursor: pointer;
    color: ${Colors.primary};
  }
  
  strong {
    color: ${Colors.primary};
  }
`;

export const SuggestionDropDown: FC<SuggestionDropDownProps> = (props) => {
  const { required = false, hasError, errorMessage = 'Bitte treffen Sie eine Auswahl...' } = props;
  const [localError, setLocalError] = useState<boolean>(hasError);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<Suggestion | null>(props?.value || null);
  const [value, setValue] = useState<string>(selectedValue?.text || '');
  const [filteredSuggestions, setFilteredSuggestions] = useState<Suggestion[]>(props.suggestions);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const listener = (event: MouseEvent) => {
      const clicked = wrapperRef.current && (wrapperRef.current === event.target || wrapperRef.current.contains(event.target as Node));
      if (!clicked) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('click', listener);

    return () => {
      document.removeEventListener('click', listener);
    };
  }, []);

  useEffect(() => {
    setLocalError(hasError);
  }, [hasError]);

  useEffect(() => {
    if (value && value.length > 0) {
      setLocalError(false);
    }
  }, [value]);

  const handleLocalError = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (required && event.target.value === '') {
      setLocalError(true);
    } else {
      setLocalError(false);
    }
  };

  useEffect(() => {
    setFilteredSuggestions(props.suggestions.filter((suggestion) => value?.length > 0 && suggestion.text.toLowerCase().includes(value.toLowerCase())));
  }, [value]);

  useEffect(() => {
    if (selectedValue && selectedValue.text) {
      setValue(selectedValue.text);
    }
  }, [selectedValue]);

  useEffect(() => {
    if (props.value) {
      setSelectedValue(props.value);
    }
  }, [props.value]);

  const highlightSearchMatches = (str: string, highlightStr: string) => {
    try {
      return str?.replace(new RegExp(highlightStr?.replace('| E-Mail:', ''), 'gmi'), (substring) => `<strong>${substring}</strong>`);
    } catch (err) {
      //
    }
    return str;
  };

  return (
    <InputWrapper inline={props.inline}>
      <Label for={props.id}>{props.label}</Label>
      <DropDownWrapper ref={wrapperRef} inline={props.inline}>
        <InputOuterWrapper>
          <InputInnerWrapper>
            <StyledInput
              inline={props.inline}
              type={'text'}
              id={props.id}
              className={`${localError ? 'local-error' : ''}`}
              autoComplete={'off'}
              value={value}
              placeholder={`${props.placeholder ? props.placeholder : 'Hier eingeben'}`}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleLocalError(event);
                const nextValue = event.target.value;
                setValue(nextValue);
                setShowSuggestions(!Boolean(props.showSearchResultsOnly) || nextValue?.length > 0);
              }}
              onFocus={(event: React.FormEvent<HTMLInputElement>) => {
                if (!Boolean(props.showSearchResultsOnly) || value?.length > 0) {
                  setShowSuggestions(true);
                }
              }}>
            </StyledInput>
            <OpenSelectButton>
              <Icon icon={'search'} size={24} color={Colors.white}/>
            </OpenSelectButton>
          </InputInnerWrapper>
          {
            showSuggestions &&
            <SuggestionList inline={props.inline}>
              {
                filteredSuggestions.length > 0 ? filteredSuggestions.map((suggestion) => {
                  return (
                    <SuggestionListItem
                      onClick={(event) => {
                        props.onChange(suggestion);
                        setShowSuggestions(false);
                      }}
                      key={suggestion.id}
                      dangerouslySetInnerHTML={{ __html: highlightSearchMatches(suggestion.text, value) }}
                    />
                  )
                }) : 'kein Ergebnis gefunden'
              }
            </SuggestionList>
          }
        </InputOuterWrapper>
        {
          localError &&
          <ErrorText>{errorMessage}</ErrorText>
        }
      </DropDownWrapper>
    </InputWrapper>
  );
};
